import { useEffect, useState } from 'react'
import { supabase, isDemoMode } from '../lib/supabase'
import DataProtectionNotice from '../components/DataProtectionNotice'

// Modes:
//  - 'signin'   — normal email + password
//  - 'invite'   — first visit from an invite email OR existing session with
//                 no password_set flag; user must set a password
//  - 'recovery' — visit from "forgot password" email; user must set a new one
//
// authMode comes from App.jsx, which decides between:
//   (a) window.__authMode set by the inline script in index.html (URL hash hint)
//   (b) boot-time check finding a session for a user with no password_set
// (b) is the robust path — works even if email clients strip URL fragments.

export default function Login({ onLogin, brand, authMode = 'signin' }) {
  const [mode] = useState(authMode)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [password2, setPassword2] = useState('')
  const [dpAccepted, setDpAccepted] = useState(false)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const [inviteReady, setInviteReady] = useState(false)

  // For invite/recovery: wait for supabase-js to finish processing the hash
  // and create a session, then pre-fill the email so the user can see whose
  // account they're activating.
  useEffect(() => {
    if (mode === 'signin' || isDemoMode || !supabase) return
    let cancelled = false
    const wait = async () => {
      // Poll up to ~3s for the session to materialise (detectSessionInUrl is async)
      for (let i = 0; i < 30; i++) {
        const { data } = await supabase.auth.getSession()
        if (data.session) {
          if (cancelled) return
          setEmail(data.session.user.email || '')
          setInviteReady(true)
          return
        }
        await new Promise(r => setTimeout(r, 100))
      }
      if (!cancelled) setError('Invite link has expired or already been used. Ask the admin to re-send.')
    }
    wait()
    return () => { cancelled = true }
  }, [mode])

  const submit = async (e) => {
    e.preventDefault()
    setError('')
    setBusy(true)
    try {
      if (isDemoMode) {
        onLogin({ email, name: email.split('@')[0] })
        return
      }

      // Invite / recovery: set the password on the existing session AND
      // flip the password_set flag so App.jsx stops routing this user back
      // to the set-password screen on subsequent visits.
      if (mode !== 'signin') {
        if (!inviteReady) throw new Error('Still loading your invite — please wait a moment.')
        if (password.length < 6) throw new Error('Password must be at least 6 characters.')
        if (password !== password2) throw new Error('Passwords don\'t match.')
        const { error: updErr } = await supabase.auth.updateUser({
          password,
          data: { password_set: true },
        })
        if (updErr) throw updErr
        const { data: { user } } = await supabase.auth.getUser()
        // Clean any lingering hint so a refresh doesn't put us back in invite mode
        delete window.__authMode
        window.history.replaceState(null, '', window.location.pathname)
        onLogin({
          email: user.email,
          name: user.user_metadata?.name || user.email.split('@')[0],
          id: user.id,
        })
        return
      }

      // Normal sign-in
      const { data, error } = await supabase.auth.signInWithPassword({ email, password })
      if (error) throw error
      // Backfill password_set for existing users (created via SQL or older
      // invites that pre-date this flag). Anyone who can sign in with a
      // password obviously has one set — silently mark them so they don't
      // get force-routed to the set-password screen later.
      if (!data.user.user_metadata?.password_set) {
        supabase.auth.updateUser({ data: { password_set: true } }).catch(() => {})
      }
      onLogin({
        email: data.user.email,
        name: data.user.user_metadata?.name || data.user.email.split('@')[0],
        id: data.user.id,
      })
    } catch (err) {
      setError(err.message || 'Sign-in failed')
    } finally {
      setBusy(false)
    }
  }

  const isInviteOrRecovery = mode !== 'signin'
  const heading = mode === 'invite' ? 'Welcome — set a password'
                : mode === 'recovery' ? 'Choose a new password'
                : (brand?.name || 'WG Orders')
  const subheading = mode === 'invite' ? 'Pick something memorable. You\'ll use this every time you sign in.'
                   : mode === 'recovery' ? 'Pick a new password to replace the old one.'
                   : 'Order Platform'

  // Per-brand login background image (optional). If set, the login card
  // floats over the image with a soft dark overlay for contrast. If not,
  // fall back to a subtle brand-tinted gradient — never plain grey, so
  // the login screen looks intentional even before a client customises.
  const hasBg = !!brand?.login_bg_url
  const bgStyle = hasBg
    ? {
        backgroundImage: `linear-gradient(rgba(0,0,0,0.35), rgba(0,0,0,0.55)), url(${brand.login_bg_url})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      }
    : {
        background: 'linear-gradient(135deg, rgb(var(--brand-light) / 0.5) 0%, rgb(var(--brand) / 0.15) 100%)',
      }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 py-8" style={bgStyle}>
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl p-8 backdrop-blur-sm my-auto">
        <div className="text-center mb-6">
          {brand?.logo_url
            ? <img src={brand.logo_url} alt={brand.name || 'Logo'} className="mx-auto mb-3" style={{ maxWidth: 180, height: 'auto' }} />
            : <div className="text-2xl font-semibold text-brand-dark mb-1">{brand?.name || 'WG Orders'}</div>
          }
          {isInviteOrRecovery
            ? <>
                <h1 className="text-lg font-semibold text-gray-800 mt-3">{heading}</h1>
                <p className="text-sm text-gray-500 mt-1">{subheading}</p>
              </>
            : <p className="text-sm text-gray-500">{subheading}</p>
          }
        </div>

        <form onSubmit={submit} className="space-y-3">
          <input
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            placeholder="Email"
            className="input-field"
            required
            autoFocus={!isInviteOrRecovery}
            readOnly={isInviteOrRecovery}
            disabled={isInviteOrRecovery && !inviteReady}
          />
          <input
            type="password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            placeholder={isInviteOrRecovery ? 'New password (min 6 chars)' : 'Password'}
            className="input-field"
            required={!isDemoMode}
            autoFocus={isInviteOrRecovery && inviteReady}
            disabled={isInviteOrRecovery && !inviteReady}
          />
          {isInviteOrRecovery && (
            <input
              type="password"
              value={password2}
              onChange={e => setPassword2(e.target.value)}
              placeholder="Confirm new password"
              className="input-field"
              required
              disabled={!inviteReady}
            />
          )}
          {error && <p className="text-sm text-red-600">{error}</p>}

          <DataProtectionNotice accepted={dpAccepted} onChange={setDpAccepted} compact />

          <button
            type="submit"
            disabled={busy || !dpAccepted || (isInviteOrRecovery && !inviteReady)}
            title={dpAccepted ? '' : 'Please acknowledge the Data Protection Notice first'}
            className={`w-full justify-center ${dpAccepted ? 'btn-primary' : 'btn-primary opacity-50 cursor-not-allowed'}`}
          >
            {busy
              ? (isInviteOrRecovery ? 'Saving…' : 'Signing in…')
              : (isInviteOrRecovery ? 'Save password & sign in' : 'Sign In')
            }
          </button>
          <p className="text-[11px] text-gray-400 text-center">
            Access is subject to acknowledgement of these requirements.
          </p>
          {isDemoMode && (
            <p className="text-xs text-gray-400 text-center pt-2">
              Demo mode — Supabase not configured. Any email signs you in.
            </p>
          )}
          {isInviteOrRecovery && !inviteReady && !error && (
            <p className="text-xs text-gray-400 text-center pt-2">Loading your invite…</p>
          )}
        </form>
      </div>
    </div>
  )
}
